<template>
  <a-card size="small" class="intercompany-status-card">
    <div slot="title" class="flex align-items-center gap-2">
      <span>ACH payment</span>
      <a-tag v-if="invoice" class="status-tag" :color="statusPresentation.color">
        {{ statusPresentation.title }}
      </a-tag>
    </div>

    <a-button slot="extra" size="small" :loading="loading" @click="$emit('refresh')">Refresh</a-button>

    <a-alert
      v-if="statusUnavailable"
      type="warning"
      message="ACH payment status could not be loaded. Refresh the Payment Request to try again."
    />

    <template v-else-if="invoice">
      <a-alert :type="statusPresentation.alertType">
        <span slot="message">{{ statusPresentation.title }}</span>
        <div slot="description">
          <p class="status-description">{{ statusMessage }}</p>
          <a-button v-if="showManageBankAccount" size="small" type="primary" @click="$emit('manage-bank-account')">
            Manage Bank Account
          </a-button>
        </div>
      </a-alert>

      <a-descriptions class="payment-breakdown" size="small">
        <a-descriptions-item label="Amount">
          {{ formatMoney(invoice.amount, invoice.currency) }}
        </a-descriptions-item>
        <a-descriptions-item label="Platform fee">
          {{ formatMoney(invoice.application_fee_amount, invoice.currency) }}
        </a-descriptions-item>
        <a-descriptions-item label="Agency receives">
          {{ formatMoney(invoice.payee_net_amount, invoice.currency) }}
        </a-descriptions-item>
        <a-descriptions-item label="Payment method">ACH debit</a-descriptions-item>
        <a-descriptions-item label="Attempts">
          {{ invoice.attempt_count }} of {{ invoice.max_attempts }}
        </a-descriptions-item>
      </a-descriptions>
    </template>
  </a-card>
</template>

<script>
import { Alert, Button, Card, Descriptions, Tag } from 'ant-design-vue'
import Util from '@/util'

const STATUS_PRESENTATION = Object.freeze({
  prepared: {
    title: 'Ready to process',
    color: '#3b82f6',
    alertType: 'info'
  },
  processing: {
    title: 'Payment processing',
    color: '#3b82f6',
    alertType: 'info'
  },
  succeeded: {
    title: 'Payment received',
    color: '#10b981',
    alertType: 'success'
  },
  retry_scheduled: {
    title: 'Retry scheduled',
    color: '#f59e0b',
    alertType: 'warning'
  },
  action_required: {
    title: 'Action required',
    color: '#ef4444',
    alertType: 'error'
  },
  failed: {
    title: 'Payment not collected',
    color: '#ef4444',
    alertType: 'error'
  },
  manual_review: {
    title: 'Payment needs review',
    color: '#6c69ff',
    alertType: 'warning'
  }
})

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-card': Card,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tag': Tag
  },
  props: {
    invoice: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    statusUnavailable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isPayerProject() {
      return Number(this.$store.state.applicationState.currentProject?.id) === Number(this.invoice?.payer_project_id)
    },
    statusPresentation() {
      return STATUS_PRESENTATION[this.invoice?.status] || STATUS_PRESENTATION.manual_review
    },
    showManageBankAccount() {
      return this.invoice?.status === 'action_required' && this.isPayerProject
    },
    statusMessage() {
      const status = this.invoice?.status

      if (status === 'prepared') {
        return 'Payment is ready for ACH processing. Worker payouts for these shifts remain on hold.'
      }
      if (status === 'processing') {
        return 'Payment is processing by ACH. Worker payouts for these shifts remain on hold until the payment clears.'
      }
      if (status === 'succeeded') {
        return 'The ACH payment was received. The payment hold for these shifts has been released.'
      }
      if (status === 'retry_scheduled') {
        const retryDate = this.formatRetryDate(this.invoice.next_retry_at)

        if (this.isPayerProject) {
          return `Payment could not be collected because the bank account had insufficient funds. The next ACH attempt is scheduled for ${retryDate}. ACH rules allow up to ${this.invoice.max_attempts} attempts total. Keep sufficient funds available before then. Worker payouts for these shifts remain on hold.`
        }

        return `Payment is delayed because the paying company’s ACH debit did not clear. Worker payouts for these shifts remain on hold. The next attempt is scheduled for ${retryDate}.`
      }
      if (status === 'action_required') {
        return this.isPayerProject
          ? 'The connected bank account is closed or no longer authorized. Reconnect a bank account to continue this payment. Worker payouts for these shifts remain on hold.'
          : 'Payment is delayed because the paying company must reconnect its bank account. Worker payouts for these shifts remain on hold.'
      }
      if (status === 'failed' && this.invoice.retry_exhausted_at) {
        return this.isPayerProject
          ? `Payment could not be collected after ${this.invoice.max_attempts} ACH attempts. Automatic retries have stopped. Add funds and contact support to settle this payment. Worker payouts for these shifts remain on hold.`
          : `Payment was not collected after ${this.invoice.max_attempts} ACH attempts. Automatic retries have stopped, and worker payouts for these shifts remain on hold.`
      }
      if (status === 'failed') {
        return this.isPayerProject
          ? 'Payment could not be collected. Automatic processing has stopped. Contact support to review this payment. Worker payouts for these shifts remain on hold.'
          : 'Payment could not be collected. Worker payouts for these shifts remain on hold while the paying company resolves the payment.'
      }

      return 'Payment status needs review. Worker payouts for these shifts remain on hold. Contact support before taking further payment action.'
    }
  },
  methods: {
    formatMoney(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase()
      }).format((amount || 0) / 100)
    },
    formatRetryDate(date) {
      return Util.convertDateTimeToCurrentTimeZoneWithFormat(date, 'MMM D, YYYY [at] h:mm A') || 'a later time'
    }
  }
}
</script>

<style scoped>
.intercompany-status-card {
  border-color: var(--border-color-base);
}

.status-tag {
  margin: 0;
}

.status-description {
  margin-bottom: 8px;
}

.payment-breakdown {
  margin-top: 12px;
}
</style>
